import React, { useState } from "react";
import CustomTabs from "../ui/CustomTabs";
import { useModalStack } from "../../utils/modalContext";
import { fetchItems } from "../../utils/fetchItemData";
import AvatarRep from "./AvatarRep";
import ReputationQuests from "./RepQuests";
import CurrencyData from "./Currencies";
import DiscItems from "./DiscItems";
import Levels from "./Levels";
import PetTypes from "./PetTypes";
import SkillsBreakdown from "./Skills";
import AIVeronica from "../liveOpsComponents/AIVeronica";
import NeonZone from "../liveOpsComponents/NeonZone";
import PetDrops from "./PetDrops";
import DurabilityGuide from "./DurabilityItems";

const componentMap = {
  AvatarRep: <AvatarRep />,
  ReputationTasks: <ReputationQuests />,
  Currencies: <CurrencyData />,
  DiscItems: <DiscItems />,
  Levels: <Levels />,
  PetTypes: <PetTypes />,
  Skills: <SkillsBreakdown />,
  AIVeronica: <AIVeronica />,
  NeonZone: <NeonZone />,
  petdrops: <PetDrops />,
  Durability: <DurabilityGuide />,
};

const GuideContentRenderer = ({ content }) => {
  const { pushModal } = useModalStack();
  const isTopLevelSubsectionsOnly = content.every((block) => block.type === "subsection");
  const [activeSubTab, setActiveSubTab] = useState(0);

  const handleOpenModal = async (itemId) => {
    try {
      const itemData = await fetchItems({ itemId });
      if (!itemData || !itemData[0]) return;

      pushModal({
        id: `item-${itemId}`,
        type: "itemModal",
        props: {
          item: itemData[0],
          onClose: () => {},
          showNavigation: false,
          onNavigate: null,
          totalItems: 1,
          index: null,
        },
      });
    } catch (err) {
      console.error("Failed to fetch item:", err);
    }
  };

  if (!content || !Array.isArray(content)) return null;

  // ✅ Handle subsection tabs all at once, BEFORE the .map
  if (isTopLevelSubsectionsOnly) {
    const subsectionTabs = content.map((subsection, idx) => ({
      key: `subsection-${idx}`,
      label: subsection.title,
      component: (
        <div className="">
          <GuideContentRenderer content={subsection.content || []} />
        </div>
      ),
    }));

    return (
      <CustomTabs
        tabs={subsectionTabs}
        activeTab={subsectionTabs[activeSubTab]?.key}
        onTabChange={(key) => {
          const newIndex = subsectionTabs.findIndex((tab) => tab.key === key);
          setActiveSubTab(newIndex);
        }}
      />
    );
  }

  // 🧱 Normal block-by-block rendering
  return content.map((block, idx) => {
    if (block.type === "subdivision") {
      return (
        <div key={`subdivision-${idx}`} className=" wiki-sections">
          <h4 className="wiki-section-title ">{block.title}</h4>
          <GuideContentRenderer content={block.content || []} />
        </div>
      );
    }

    switch (block.type) {
      case "text":
        return (
          <div
            key={`text-${idx}`}
            className="wiki-text"
            dangerouslySetInnerHTML={{
              __html: typeof block.data === "function" ? block.data() : block.data,
            }}
          />
        );

      case "image":
        return (
          <div key={`image-${idx}`} className="relative mx-3 my-4 group w-fit">
            {/* Main image */}
            <img
              src={block.data?.img}
              alt={block.data?.alt || "Guide image"}
              className="rounded max-w-full h-60 object-cover transition-transform duration-200 ease-in-out"
            />

            {/* Enlarged image on hover */}
            <div className="absolute top-0 left-0 z-50 hidden group-hover:flex items-center justify-center w-full h-full">
              <img
                src={block.data?.img}
                alt={block.data?.alt || "Guide image"}
                className="max-w-[300px] max-h-[300px] rounded shadow-lg border border-gray-300 bg-white scale-150"
              />
            </div>

            {/* Image credit */}
            {block.data?.credit && (
              <p className="text-sm text-gray-500 mt-1">
                Credit:{" "}
                <a href={block.data.credit.url} className="wiki-links">
                  {block.data.credit.name}
                </a>
              </p>
            )}
          </div>
        );

      case "gallery":
        return (
          <div key={`gallery-${idx}`} className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 my-4 relative">
            {(block.images || []).map((img, i) => (
              <div key={i} className="relative group">
                {/* Normal Image */}
                <img src={img.src} alt={img.alt} className="rounded w-full h-40 object-cover transition-transform duration-200 ease-in-out" />

                {/* Enlarged on hover */}
                <div className="absolute z-50 hidden group-hover:flex items-center justify-center">
                  <img
                    src={img.src}
                    alt={img.alt}
                    className="max-w-[300px] max-h-[300px] rounded shadow-lg transition-transform scale-105 border border-gray-300 bg-white"
                  />
                </div>

                {/* Credit */}
                {img.credit && (
                  <p className="text-sm text-[var(--text4-color)] mt-1">
                    Credit:{" "}
                    <a href={img.credit.url} className="wiki-links">
                      {img.credit.name}
                    </a>
                  </p>
                )}
              </div>
            ))}
          </div>
        );

      case "component":
        return (
          <div key={`component-${idx}`} className="my-4">
            {componentMap[block.data] || <p>Unknown component: {block.data}</p>}
          </div>
        );

      case "list":
        return (
          <ul key={`list-${idx}`} className="wiki-text pl-6 my-2 space-y-1">
            {block.data?.split("\n").map((item, i) => (
              <li key={i} className="wiki-list">
                {item.trim()}
              </li>
            ))}
          </ul>
        );

      case "inlineLink":
        let modifiedDescription = block.data.description;

        if (Array.isArray(block.data.text) && Array.isArray(block.data.itemId)) {
          block.data.text.forEach((text, i) => {
            const itemId = block.data.itemId[i];
            const safeText = text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); // escape regex
            const regex = new RegExp(safeText, "g");
            modifiedDescription = modifiedDescription.replace(regex, `<span class="modal-click" data-item-id="${itemId}">${text}</span>`);
          });
        } else if (typeof block.data.text === "string") {
          const safeText = block.data.text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
          const regex = new RegExp(safeText, "g");
          modifiedDescription = modifiedDescription.replace(regex, `<span class="modal-click" data-item-id="${block.data.itemId}">${block.data.text}</span>`);
        }

        return (
          <div
            key={`inlineLink-${idx}`}
            className="wiki-text"
            dangerouslySetInnerHTML={{ __html: modifiedDescription }}
            onClick={(e) => {
              const target = e.target;
              if (target.classList.contains("modal-click")) {
                const itemId = target.getAttribute("data-item-id");
                handleOpenModal(itemId);
              }
            }}
          />
        );

      case "table":
        const rowSpanKey = block.usesRowSpan;

        return (
          <div key={`table-${idx}`} className="overflow-x-auto my-4 wiki-text">
            <table className="market-table max-w-fit border-collapse border border-gray-300">
              {block.data?.length > 0 && (
                <>
                  <thead className="bg-gray-100">
                    <tr>
                      {Object.keys(block.data[0])
                        .filter((key) => key !== "rowSpan")
                        .map((key) => (
                          <th key={key} className="px-4 py-2 text-left border capitalize">
                            {key}
                          </th>
                        ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rowSpanKey
                      ? // Rowspan version
                        Object.entries(
                          block.data.reduce((acc, row) => {
                            const group = row[rowSpanKey];
                            acc[group] = acc[group] || [];
                            acc[group].push(row);
                            return acc;
                          }, {})
                        ).flatMap(([group, rows]) =>
                          rows.map((row, i) => (
                            <tr key={`${group}-${i}`} className={i % 2 ? "bg-gray-50" : "bg-white"}>
                              {Object.entries(row).map(([key, value]) => {
                                if (key === "rowSpan") return null;
                                if (key === rowSpanKey) {
                                  return i === 0 ? (
                                    <td key={key} rowSpan={rows.length} className="px-4 py-2 border">
                                      {value}
                                    </td>
                                  ) : null;
                                }
                                return (
                                  <td key={key} className="px-4 py-2 border">
                                    {typeof value === "string" && value.includes("<") ? (
                                      <div dangerouslySetInnerHTML={{ __html: value }} />
                                    ) : Array.isArray(value) ? (
                                      value.map((el, j) => <div key={j}>{el}</div>)
                                    ) : (
                                      value
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          ))
                        )
                      : // Normal version
                        block.data.map((row, i) => (
                          <tr key={i} className={i % 2 ? "bg-gray-50" : "bg-white"}>
                            {Object.entries(row).map(
                              ([key, value]) =>
                                key !== "rowSpan" && (
                                  <td key={key} className="px-4 py-2 border">
                                    {typeof value === "string" && value.includes("<") ? (
                                      <div dangerouslySetInnerHTML={{ __html: value }} />
                                    ) : Array.isArray(value) ? (
                                      value.map((el, j) => <div key={j}>{el}</div>)
                                    ) : (
                                      value
                                    )}
                                  </td>
                                )
                            )}
                          </tr>
                        ))}
                  </tbody>
                </>
              )}
            </table>
          </div>
        );

      case "cards":
        // Sort cards by date (newest first)
        const sortedCards = [...block.data].sort((a, b) => {
          const dateA = new Date(a.datestarted);
          const dateB = new Date(b.datestarted);
          return dateB - dateA; // Newest first
        });

        return (
          <div key={`card-${idx}`} className="mb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sortedCards.map((card) => (
                <div
                  key={card.id}
                  className="bg-[var(--panel-sections)] rounded-lg shadow-md overflow-hidden border border-[var(--border)] hover:shadow-lg transition-shadow"
                >
                  <div className="p-4">
                    <h3 className="text-lg font-bold text-[var(--text-color)] mb-2">{card.title}</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-start">
                        <span className="card-title">Date:</span>
                        <span className="card-descriptions">
                          {card.datestarted} - {card.dateended}
                        </span>
                      </div>

                      <div className="flex items-start">
                        <span className="card-title">Prizes:</span>
                        <span className="card-descriptions">{card.prizes}</span>
                      </div>

                      <div className="flex items-start">
                        <span className="card-title">Environment:</span>
                        <span className="card-descriptions">{card.env}</span>
                      </div>

                      {card.howtoplay && (
                        <div className="pt-2 mt-2 border-t border-[var(--border)]">
                          <h4 className="card-title">How to Play:</h4>
                          <p className="card-descriptions" dangerouslySetInnerHTML={{ __html: card.howtoplay }} />
                        </div>
                      )}

                      {card.other && (
                        <div className="pt-2 mt-2 border-t border-[var(--border)]">
                          <h4 className="card-title">Other:</h4>
                          <p className="card-descriptions" dangerouslySetInnerHTML={{ __html: card.other }} />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      default:
        console.warn("Unknown block type:", block?.type);
        return null;
    }
  });
};

export default GuideContentRenderer;
